import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { TOAST_ERROR_DURATION_MS, TOAST_SUCCESS_DURATION_MS } from "@/lib/toast-durations";
import type { CreateProductionMachineRequest, UpdateProductionMachineRequest } from "@/lib/types";

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

const QUERY_KEY = ["production-machines"] as const;

export function useProductionMachines() {
  return useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => api.listProductionMachines(),
  });
}

export function useCreateProductionMachine() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateProductionMachineRequest) => api.createProductionMachine(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      toast.success("Added production machine", { duration: TOAST_SUCCESS_DURATION_MS });
    },
    onError: (error) => {
      toast.error("Failed to add production machine", {
        description: errorMessage(error),
        duration: TOAST_ERROR_DURATION_MS,
      });
    },
  });
}

export function useUpdateProductionMachine() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: UpdateProductionMachineRequest }) =>
      api.updateProductionMachine(id, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      toast.success("Saved", { duration: TOAST_SUCCESS_DURATION_MS });
    },
    onError: (error) => {
      toast.error("Failed to save changes", {
        description: errorMessage(error),
        duration: TOAST_ERROR_DURATION_MS,
      });
    },
  });
}

export function useDeleteProductionMachine() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.deleteProductionMachine(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      toast.success("Removed from registry", { duration: TOAST_SUCCESS_DURATION_MS });
    },
    onError: (error) => {
      toast.error("Failed to remove", {
        description: errorMessage(error),
        duration: TOAST_ERROR_DURATION_MS,
      });
    },
  });
}
